// Shared presentation helpers: risk pills, difficulty stars, tag pills,
// risk/status colour triples and the heatmap colour ramp. Pure UI, no data
// dependencies. Token-resolving variants read from the CSS custom
// properties defined in index.js so theme switches propagate; CHART_HEX
// holds the cached hex equivalents for chart libraries that snapshot
// colours at mount.
//
// Postgres mapping: none, this is pure presentation. The semantic level
// taxonomy (high / med / low) lines up with `users.lv` and
// `simulations_sent.status` so colour decisions stay 1:1 with the schema.
//
// Integration hook: no API surface. If colours move into a tenancy theme
// API later, only CHART_HEX needs to become a runtime lookup.
import React from 'react';

export function rpill(lv) {
    const cls = lv === 'high' ? 'rh' : lv === 'med' ? 'rm' : 'rl';
    return (
        <span className={`rpill ${cls}`}>
            {lv.charAt(0).toUpperCase() + lv.slice(1)}
        </span>
    );
}

// Difficulty 1-3 as ascending signal bars. Rising height encodes the level
// pre-attentively (reads as low -> high intensity), and the green/amber/red
// fill keeps difficulty colour-coded consistently with DiffDots in
// SimDetailPage (3 = hardest = red). Kept as a named export so existing
// `stars(n)` call sites are unchanged.
export function stars(n) {
    const fill = n >= 3 ? 'var(--red)' : n >= 2 ? 'var(--amber)' : 'var(--green)';
    return (
        <div className="stars" role="img" aria-label={`Difficulty ${n} of 3`} style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 12 }}>
            {[1, 2, 3].map((i) => (
                <span
                    key={i}
                    aria-hidden="true"
                    style={{
                        width: 4,
                        height: 4 + (i - 1) * 4,
                        borderRadius: 1,
                        background: i <= n ? fill : 'rgba(106,113,145,.25)'
                    }}
                />
            ))}
        </div>
    );
}

export function tpills(arr, flag) {
    return arr.map((t, i) => {
        const isAlert = flag && (t === 'monetary request' || t === 'implied authority');
        return (
            <span key={i} className={`tpill${isAlert ? ' al' : ''}`}>
                {t}
            </span>
        );
    });
}

// Shared risk colour triple, sourced from the CSS token set defined in
// index.js so theme switches propagate automatically. Accepts either a `lv`
// string ('high' | 'med' | 'low') or a numeric click/risk count.
export function riskColor(input) {
    let lv;
    if (typeof input === 'number') {
        lv = input >= 3 ? 'high' : input >= 1 ? 'med' : 'low';
    } else {
        lv = input;
    }
    if (lv === 'high') return { bg: 'var(--red-bg)',   text: 'var(--red-text)',   border: 'var(--red)' };
    if (lv === 'med')  return { bg: 'var(--amber-bg)', text: 'var(--amber-text)', border: 'var(--amber)' };
    return                    { bg: 'var(--green-bg)', text: 'var(--green-text)', border: 'var(--green)' };
}

// Status palette for sim engagement events. Same token strategy.
export function statusColor(status) {
    switch (status) {
        case 'clicked_2nd': return { bg: 'var(--red-bg)',   text: 'var(--red-text)' };
        case 'clicked':     return { bg: 'var(--amber-bg)', text: 'var(--amber-text)' };
        case 'reported':    return { bg: 'var(--green-bg)', text: 'var(--green-text)' };
        default:            return { bg: 'var(--surface2)', text: 'var(--muted)' };
    }
}

// Chart palette - cached strings for Recharts/Apex which don't re-read
// CSS vars on theme flip. The few places we need real hex (chart libs)
// import these so a future palette change is one file.
export const CHART_HEX = {
    red:    '#FF5252',
    amber:  '#FFCA28',
    green:  '#66BB6A',
    teal:   '#00BFA5',
    dim:    '#A0A8B8',
    muted:  '#6B7591',
    text:   '#111418',
};

// Heatmap cell colour ramp. score is expected on a 0-10 scale; light mode
// fades white->red, dark mode fades neutral->saturated red. Text colour
// switches at t > 0.65 / t > 0.4 to maintain AA contrast on the strongest
// cells. Caller must pre-normalise raw counts into the 0-10 range.
export function heatColor(score, dark) {
    const t = score / 10;
    if (dark) {
        const r = Math.round(60 + t * 172);
        const g = Math.round(60 - t * 30);
        const b = Math.round(60 - t * 30);
        const a = (0.25 + t * 0.65).toFixed(2);
        const bg = `rgba(${r},${g},${b},${a})`;
        const tc = t > 0.65 ? '#fff' : t > 0.4 ? '#ffcccc' : '#8A919F';
        return { bg, tc };
    } else {
        const r2 = 255;
        const g2 = Math.round(255 - t * 210);
        const b2 = Math.round(255 - t * 230);
        const a2 = (0.18 + t * 0.72).toFixed(2);
        const bg = `rgba(${r2},${g2},${b2},${a2})`;
        const tc = t > 0.65 ? '#7a0010' : t > 0.35 ? '#a03020' : '#6B7591';
        return { bg, tc };
    }
}
